import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import {
    getTranscription,
    startTranscription,
    TranscriptionRecord,
} from "../actions/transcription.action";

const POLL_INTERVAL_MS = 3000;

type UseTranscriptionParams = {
    uid: string;
    enabled: boolean;
    seed?: TranscriptionRecord | null;
};

export const transcriptionQueryKey = (uid: string) => ["call-transcription", uid];

export const useTranscription = ({ uid, enabled, seed }: UseTranscriptionParams) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useGlobalSnackbar();

    const query = useQuery<TranscriptionRecord>({
        queryKey: transcriptionQueryKey(uid),
        queryFn: () => getTranscription(uid),
        enabled: enabled && !!uid,
        initialData: seed ?? undefined,
        refetchInterval: (currentQuery) =>
            currentQuery.state.data?.status === "processing" ? POLL_INTERVAL_MS : false,
    });

    const start = useMutation({
        mutationFn: () => startTranscription(uid),

        onSuccess: (data) => {
            queryClient.setQueryData(transcriptionQueryKey(uid), data);
        },

        onError: () => {
            showSnackbar("Failed to start the transcription", "error");
        },
    });

    const { mutate: startMutate } = start;

    const startTranscript = useCallback(() => {
        startMutate();
    }, [startMutate]);

    const transcription = query.data ?? null;
    const status = transcription?.status ?? "none";

    return {
        transcription,
        status,
        isProcessing: status === "processing",
        isLoading: query.isLoading || start.isPending,
        hasStartError: start.isError,
        canAutoStart: start.isIdle && !query.isLoading,
        start: startTranscript,
    };
};
